"use client";
import Text from "@/components/Text";
import { useTranslation } from "@/i18n/client";
import classes from "./index.module.css";
import { Process, Segment, Phase } from "@/types/contract";
import { Product } from "@/types/enumerations";
import { Trans } from "react-i18next";
import Link from "@/components/Link";
import type { ProcessReasonType } from "@/types/processReason";
import { listKey } from "./helpers";
import { useIsContractRequest } from "@/hooks/useIsContractRequest";
import { useQueryStorage } from "@/contexts/QueryStorageContext";
import { isS3PreviousCustomer } from "@/screens/Welcome/helpers";

interface WhatToPrepareListProps {
  process: Process;
  selfService: boolean | null;
  product?: Product | null;
  processReason?: ProcessReasonType;
  phase?: string | null;
  segment?: Segment;
  contractStatus?: string | null;
}

const WhatToPrepareList = ({
  process,
  selfService,
  product,
  processReason,
  phase,
  segment,
  contractStatus,
}: WhatToPrepareListProps) => {
  const { t } = useTranslation("welcome");
  const { storedQueries } = useQueryStorage();
  const isContractPreparation = storedQueries.phase === "contract";
  const isContractRequest = useIsContractRequest();
  const isPSProcess = process === Process.PS;
  const isPSProcessRequest = isPSProcess && isContractRequest && !isContractPreparation;
  const isPSContractPreparation = isPSProcess && isContractRequest && isContractPreparation;
  const isS3NewCustomerProcess = isS3PreviousCustomer(process, contractStatus, phase);
  const phaseRequest = [Phase.REQUEST, Phase.LEAD].includes(phase ?? "");

  const keys = listKey({
    process,
    selfService,
    isPSProcessRequest,
    isPSContractPreparation,
    isS3NewCustomerProcess,
    processReason,
    product,
    phase,
    segment,
  });

  const items = t(keys, {
    returnObjects: true,
  });

  return (
    <div>
      <Text variant="subheadline_bold">
        {isPSProcess
          ? t("whatToPreparePS")
          : isContractRequest || isS3NewCustomerProcess
          ? t("whatToPrepareRequest")
          : phaseRequest
          ? t("whatToPreparePhaseRequest")
          : t("whatToPrepare")}
      </Text>
      <ul className={classes.ul}>
        {Object.entries(items).map(([name, item], index) => (
          <li key={index}>
            <Trans
              t={t}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
              i18nKey={(keys + "." + name) as any}
              components={{
                eonLink:
                  segment === Segment.B2B ? (
                    <Link href="https://www.eon.cz/zivnostnici/zakaznicka-pece/nove-odberne-misto/" key="noveOdberneMistoFirmy" />
                  ) : (
                    <Link
                      href="https://www.eon.cz/domacnosti/zakaznicka-pece/nove-odberne-misto/"
                      key="noveOdberneMistoDomacnosti"
                    />
                  ),
              }}>
              <Text variant="reading_list">{item}</Text>
            </Trans>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default WhatToPrepareList;
